import { Inject, Injectable } from '@angular/core';
import { AccountGroupApiService, SearchByAuxiliaryDataResponseInterface } from '@vendasta/account-group';
import { AuxiliaryDataFilterInterface } from '@vendasta/account-group/lib/_internal/interfaces/api.interface';
import { Contact } from '@vendasta/sales-v2';
import { LoadRequest, TableDataService } from '@vendasta/va-filter2-table';
import { BehaviorSubject, EMPTY, Observable, combineLatest, of, zip } from 'rxjs';
import { expand, map, reduce, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { FieldDataService } from '../field-data-service/field-data.service';
import { AccountsCustomFieldsData } from './auxiliary-data-accounts-table.data-model';

interface ColumnAuxiliaryData {
  accountGroupId: string;
  jsonSchema?: any;
  jsonData?: any;
}

@Injectable()
export class AccountsAuxiliaryDataTableService implements TableDataService<AccountsCustomFieldsData> {
  private readonly totalResults$$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  public readonly totalResults$: Observable<number> = this.totalResults$$.asObservable();
  private cursors: { [id: number]: string } = { 0: '' };

  constructor(
    @Inject('PARTNER_ID') private readonly partnerId$: Observable<string>,
    private accountGroupService: AccountGroupApiService,
    private fieldDataService: FieldDataService,
  ) {}

  private getAuxiliaryDataForIDs(accountGroupIds: string[]): Observable<Map<string, ColumnAuxiliaryData>> {
    if (!accountGroupIds) {
      return of(new Map());
    }
    const accountGroupAuxiliaryDataRequests$: Observable<ColumnAuxiliaryData>[] = [];
    for (const accountGroupId of accountGroupIds) {
      accountGroupAuxiliaryDataRequests$.push(
        this.partnerId$.pipe(
          take(1),
          switchMap((partnerId) => {
            return this.fieldDataService.listData(partnerId, 'business', accountGroupId, { cursor: '', pageSize: 100 });
          }),
          map(({ jsonSchema, jsonData }) => {
            const auxiliaryObjectData: ColumnAuxiliaryData = {
              accountGroupId: accountGroupId,
            };
            try {
              auxiliaryObjectData.jsonSchema = JSON.parse(jsonSchema) as unknown;
            } catch {
              auxiliaryObjectData.jsonSchema = {};
            }
            try {
              auxiliaryObjectData.jsonData = JSON.parse(jsonData) as unknown;
            } catch {
              auxiliaryObjectData.jsonData = {};
            }
            return auxiliaryObjectData;
          }),
          shareReplay({ refCount: true, bufferSize: 1 }),
        ),
      );
    }

    return zip(...accountGroupAuxiliaryDataRequests$).pipe(
      map((responses) => {
        const responseMap: Map<string, ColumnAuxiliaryData> = new Map();
        for (const response of responses) {
          responseMap.set(response.accountGroupId, response);
        }
        return responseMap;
      }),
    );
  }

  load(request: LoadRequest): Observable<AccountsCustomFieldsData[]> {
    if (request.pageIndex === 0) {
      this.cursors = { 0: '' };
    }
    const nextCursor = this.cursors[request.pageIndex] || '';
    const filterApplied = request.filters?.fieldFilter ? request.filters?.fieldFilter[0]?.filter : null;
    const searchFilters = [];
    if (filterApplied) {
      searchFilters.push(filterApplied);
    }

    const accountGroupIDs$ = this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) => {
        return this.fieldDataService.searchByAuxiliaryData(
          partnerId,
          'business',
          {
            searchFilters: searchFilters,
          },
          {
            pageSize: request.pageSize,
            cursor: nextCursor,
          },
        );
      }),
      tap((resp) => {
        this.totalResults$$.next(resp.pagingMetadata?.totalResults);
        if (resp.pagingMetadata?.hasMore) {
          this.cursors[request.pageIndex + 1] = resp.pagingMetadata?.nextCursor;
        }
      }),
      map((resp) => resp.accountGroupIds),
    );

    const accountGroupAuxDataMap$ = accountGroupIDs$.pipe(
      take(1),
      switchMap((accountGroupIDs) => this.getAuxiliaryDataForIDs(accountGroupIDs)),
    );
    const accountGroups$ = accountGroupIDs$.pipe(
      take(1),
      switchMap((accountgroups) =>
        accountgroups
          ? this.accountGroupService.getMulti({
              accountGroupIds: accountgroups,
              projectionFilter: { richData: true, accounts: true, napData: true, contactDetails: true },
            })
          : of(null),
      ),
    );

    return combineLatest([accountGroups$, accountGroupAuxDataMap$]).pipe(
      map(([resp, auxiliaryDataMap]) =>
        (resp?.accountGroups || []).map((item) => ({
          accountGroupId: item.accountGroup?.accountGroupId,
          accountName: item.accountGroup?.napData?.companyName,
          lastUpdateDate: item.accountGroup?.updated,
          address: {
            address: item.accountGroup?.napData?.address,
            address2: item.accountGroup?.napData?.address2,
            city: item.accountGroup?.napData?.city,
            country: item.accountGroup?.napData?.country,
            postalCode: item.accountGroup?.napData?.zip,
            state: item.accountGroup?.napData?.state,
          },
          primaryContact: new Contact({
            contactEmail: item.accountGroup?.contactDetails?.email,
            firstName: item.accountGroup?.contactDetails?.firstName,
            lastName: item.accountGroup?.contactDetails?.lastName,
          }),
          jsonSchema: auxiliaryDataMap.get(item.accountGroup?.accountGroupId)?.jsonSchema,
          jsonData: auxiliaryDataMap.get(item.accountGroup?.accountGroupId)?.jsonData,
        })),
      ),
    );
  }

  loadAllRowIDs(request: LoadRequest): Observable<string[]> {
    const filterApplied = request.filters?.fieldFilter ? request.filters?.fieldFilter[0]?.filter : null;
    const searchFilters = [];
    if (filterApplied) {
      searchFilters.push(filterApplied);
    }
    const filter: AuxiliaryDataFilterInterface = {
      searchFilters,
    };

    const getNextPage = (
      lastResponse: SearchByAuxiliaryDataResponseInterface,
    ): Observable<SearchByAuxiliaryDataResponseInterface> => {
      if (!lastResponse.pagingMetadata.hasMore) {
        return EMPTY;
      }
      const nextCursor = lastResponse.pagingMetadata.nextCursor;
      return this.partnerId$.pipe(
        take(1),
        switchMap((partnerId) =>
          this.fieldDataService.searchByAuxiliaryData(partnerId, 'business', filter, {
            pageSize: request.pageSize,
            cursor: nextCursor,
          }),
        ),
      );
    };

    return this.partnerId$.pipe(
      take(1),
      switchMap((partnerId) =>
        this.fieldDataService.searchByAuxiliaryData(partnerId, 'business', filter, {
          pageSize: request.pageSize,
          cursor: '',
        }),
      ),
      expand(getNextPage),
      map((resp) => resp?.accountGroupIds || []),
      reduce((acc: string[], cur) => acc.concat(cur), []),
    );
  }
}
